import { Link } from "react-router-dom";
import { Zap, TrendingUp, TrendingDown, Activity } from "lucide-react";
import { useArticlePath } from "@/lib/i18n";
import { COLORS } from "@/components/home/LayerIndexCard";
import { sectorTrack } from "./SectorHero";

const CARD = "rounded-xl border border-white/[0.08] bg-[#0b0c10] p-4 sm:p-5";

export const CryptoIntelligence = ({ intel, title = "Crypto Intelligence", testid = "crypto-intelligence", sector = "crypto" }: {
  intel: any; title?: string; testid?: string; sector?: string;
}) => {
  const toArticle = useArticlePath();
  if (!intel) return null;
  const { what_changed = [], positive = [], negative = [], signal } = intel;
  const cards = [what_changed.length, positive.length, negative.length, signal ? 1 : 0].filter(Boolean).length;
  if (!cards) return null;
  return (
    <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-10" data-testid={testid}>
      <div className="flex items-baseline justify-between gap-3 mb-4">
        <h2 className="font-heading font-bold text-white text-lg flex items-center gap-2.5">
          <Zap aria-hidden className="h-[18px] w-[18px] text-[#A78BFA]" />
          {title}
        </h2>
        <span className="font-mono text-[9px] uppercase tracking-widest text-gray-600 hidden sm:block">
          {intel.provenance}
        </span>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {what_changed.length > 0 && (
          <div className={`${CARD} sm:col-span-2 lg:col-span-2`} data-testid="intel-what-changed">
            <p className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-[#00E5FF]">What Changed Today</p>
            <ul className="mt-3 space-y-2.5">
              {what_changed.map((w: any) => (
                <li key={w.slug} className="flex gap-2.5 items-start">
                  <span aria-hidden className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-[#7C3AED]" />
                  <Link to={toArticle(w.slug)} onClick={() => sectorTrack("intelligence_click", w.slug, sector)}
                    className="text-[13.5px] text-gray-200 leading-snug hover:text-[#00E5FF] transition-colors line-clamp-2">
                    {w.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
        {(positive.length > 0 || negative.length > 0) && (
          <div className={CARD} data-testid="intel-drivers">
            <p className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-[#A78BFA]">Sentiment Drivers</p>
            <ul className="mt-3 space-y-2">
              {positive.map((p: string, i: number) => (
                <li key={`p${i}`} className="flex items-start gap-2 text-[12.5px] text-gray-300 leading-snug">
                  <TrendingUp aria-hidden className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[#34D399]" />
                  {p}
                </li>
              ))}
              {negative.map((n: string, i: number) => (
                <li key={`n${i}`} className="flex items-start gap-2 text-[12.5px] text-gray-300 leading-snug">
                  <TrendingDown aria-hidden className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[#EF4444]" />
                  {n}
                </li>
              ))}
            </ul>
          </div>
        )}
        {signal && (
          <div className={`${CARD} flex flex-col`} data-testid="intel-signal">
            <p className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-[#A78BFA] flex items-center gap-1.5">
              <Activity aria-hidden className="h-3 w-3" />
              Layer Index Signal
            </p>
            <div className="flex items-baseline gap-2.5 mt-3">
              <span className="font-mono text-3xl font-bold text-white">{signal.value}</span>
              <span className="font-mono text-[11px] font-bold uppercase tracking-[0.18em]"
                style={{ color: COLORS[signal.label] || "#A3A3A3" }}>
                {signal.label}
              </span>
            </div>
            <p className="text-[12.5px] text-gray-400 leading-relaxed mt-2.5">{signal.text}</p>
            <Link to="/layer-index" onClick={() => sectorTrack("intelligence_click", "layer_index", sector)}
              className="font-mono text-[10.5px] font-bold text-[#00E5FF] hover:text-white transition-colors mt-auto pt-3">
              Full methodology →
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};
